// src/offline/offlineStoreRegistry.js
import localforage from 'localforage';
import { getLocalForageStore } from './localforageStores';

// Create a dedicated instance for the registry
const registryStore = localforage.createInstance({
  name: 'OfflineStoreRegistry',
  storeName: 'stores', // Optional: define a specific store name within the DB
});

const REGISTRY_KEY = 'registeredStores';

/**
 * Registers a store name and its redirect link if it doesn't exist,
 * or updates the redirect link if it does.
 * @param {string} storeName - The name of the store.
 * @param {string} redirectLink - The redirect link for the store.
 */
export const registerStore = async (storeName, redirectLink, strict = false) => {
  if (!storeName) {
    console.warn('Attempted to register a store without a name.');
    return;
  }
  try {
    const currentRegistry = (await registryStore.getItem(REGISTRY_KEY)) || [];
    const storeIndex = currentRegistry.findIndex(
      (entry) => entry.storeName === storeName,
    );

    let updatedRegistry;
    if (storeIndex > -1) {
      // Update existing entry if redirectLink differs
      if (currentRegistry[storeIndex].redirectLink !== redirectLink) {
        console.log(`Updating redirect link for store: ${storeName}`);
        currentRegistry[storeIndex].redirectLink = redirectLink;
        updatedRegistry = [...currentRegistry]; // Create a new array reference
      } else {
        // No change needed
        return;
      }
    } else {
      // Add new entry
      console.log(`Registering new store: ${storeName}`);
      updatedRegistry = [
        ...currentRegistry,
        { storeName, redirectLink: redirectLink || '' }, // Ensure redirectLink is stored
      ];
    }

    await registryStore.setItem(REGISTRY_KEY, updatedRegistry);
  } catch (error) {
    console.error('Error registering offline store:', error);
    if (strict) throw error;
  }
};

/**
 * Retrieves the list of all registered stores with their redirect links.
 * @returns {Promise<Array<{storeName: string, redirectLink: string}>>}
 */
export const getAllRegisteredStores = async () => {
  try {
    const registry = (await registryStore.getItem(REGISTRY_KEY)) || [];
    return registry;
  } catch (error) {
    console.error('Error getting registered stores:', error);
    return [];
  }
};

/**
 * Retrieves the redirect link for a specific store.
 * @param {string} storeName - The name of the store.
 * @returns {Promise<string|null>} The redirect link or null if not found.
 */
export const getRedirectLinkForStore = async (storeName) => {
  try {
    const registry = await getAllRegisteredStores();
    const storeEntry = registry.find((entry) => entry.storeName === storeName);
    return storeEntry ? storeEntry.redirectLink : null;
  } catch (error) {
    console.error(`Error getting redirect link for ${storeName}:`, error);
    return null;
  }
};

/**
 * Retrieves pending operations for a specific store.
 * @param {string} storeName - The name of the store.
 * @returns {Promise<Array<Object>>} An array of pending operations.
 */
export const getPendingOperationsForStore = async (storeName) => {
  if (!storeName) return [];
  try {
    // Recreate the instance name exactly as in useOfflineSync
    const operationStore = getLocalForageStore(`OfflineOperations_${storeName}`);
    const operations = (await operationStore.getItem('operations')) || [];
    return operations;
  } catch (error) {
    console.error(`Error getting pending operations for ${storeName}:`, error);
    return [];
  }
};

/**
 * Retrieves all pending operations across all registered stores.
 * @returns {Promise<Object<string, Array<Object>>>} An object where keys are store names
 * and values are arrays of pending operations for that store.
 */
export const getAllPendingOperations = async (stores = null) => {
  const allOps = {};
  try {
    const registeredStores = stores || await getAllRegisteredStores();
    for (const storeInfo of registeredStores) {
      allOps[storeInfo.storeName] = await getPendingOperationsForStore(
        storeInfo.storeName,
      );
    }
    return allOps;
  } catch (error) {
    console.error('Error getting all pending operations:', error);
    return {}; // Return empty object on error
  }
};

/**
 * Checks the live operation queues and rejects if their state cannot be read.
 * This is used for decisions that must not proceed when pending work may exist.
 */
export const hasPendingOperations = async () => {
  const registeredStores = (await registryStore.getItem(REGISTRY_KEY)) || [];

  for (const { storeName } of registeredStores) {
    if (!storeName) continue;

    const operationStore = getLocalForageStore(`OfflineOperations_${storeName}`);
    const operations = (await operationStore.getItem('operations')) || [];

    if (operations.length > 0) return true;
  }

  return false;
};

/**
 * Gets the total count of pending operations across all registered stores.
 * @returns {Promise<number>} The total count.
 */
export const getTotalPendingOperationsCount = async () => {
  let totalCount = 0;
  try {
    const allOps = await getAllPendingOperations(); // Reuse existing function
    Object.values(allOps).forEach((opsArray) => {
      totalCount += opsArray.length;
    });
    return totalCount;
  } catch (error) {
    console.error('Error calculating total pending operations count:', error);
    return 0;
  }
};

/**
 * Gets the count of pending operations for a specific store.
 * @param {string} storeName - The name of the store.
 * @returns {Promise<number>} The count for the specific store.
 */
export const getPendingOperationsCountForStore = async (storeName) => {
  try {
    const ops = await getPendingOperationsForStore(storeName);
    return ops.length;
  } catch (error) {
    // Error already logged in getPendingOperationsForStore
    return 0;
  }
};

/**
 * Clears the registry (use with caution).
 */
export const clearRegistry = async () => {
  try {
    await registryStore.removeItem(REGISTRY_KEY);
    console.log('Offline store registry cleared.');
  } catch (error) {
    console.error('Error clearing offline store registry:', error);
  }
};
